/** @jsxImportSource @emotion/react */
import { Drawer as MuiDrawer, useMediaQuery } from '@mui/material';
import { breakpoints, Buttons } from '@zip/business-components';
import * as Icons from '@zip/icons';
import { useSnackbar } from 'contexts';
import { PageAction } from 'enums';
import { useFormik } from 'formik';
import { theme } from 'global';
import { useFetch } from 'hooks';
import * as illustrations from 'public/illustrations';
import { FC, useEffect, useRef, useState } from 'react';
import { logEvent } from 'utils';
import * as styles from './CustomiseOffers.styles';
import CustomiseOffersCheckbox from './CustomiseOffersCheckbox';
import { CustomiseOffersOptions } from './CustomiseOffersOptions';

interface CustomiseOffersSidebarProps {
  open: boolean;
  onClose: () => void;
}

interface OfferData {
  id: number;
  title: string;
  details: string;
  selected: boolean;
  requiredErrorMessage: string;
}

interface FormValues {
  offers: OfferData[];
}

interface FormErrors {
  offers: Record<number, { details: string }>;
}

const trackSidebarActivity = (
  activity: 'open' | 'submit' | 'closeWithoutSubmit'
): void => {
  logEvent(PageAction.productsAndOffersCustomiseOffersSidebarOps, {
    activity: activity,
  });
};

const CustomiseOffersSidebar: FC<CustomiseOffersSidebarProps> = ({
  open: currentOpen,
  onClose,
}) => {
  const { post } = useFetch();
  const Snackbar = useSnackbar();

  const Title = 'Customise your offers';
  const Explanation =
    'You can easily request changes to your Zip account. Our Merchant Experience team will get back to you within 2 business days.';
  const SubmittedTitle = 'Request submitted';
  const SubmittedDetails =
    'Our Merchant Experience team will contact you via email within 2 business days.';

  const [submitState, setSubmitState] = useState<
    'editing' | 'submitting' | 'submittedOk' | 'submittedError'
  >('editing');

  const previousOpen = useRef<boolean>(false);
  const isMobile = useMediaQuery(breakpoints.down(theme.breaks.sm));
  const [buttonOn, setButtonOn] = useState<boolean>(false);
  const autoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const initialValues: FormValues = {
    offers: CustomiseOffersOptions.map((option) => ({
      id: option.id,
      title: option.title,
      selected: option.selected,
      details: option.details.text,
      requiredErrorMessage: option.requiredErrorMessage,
    })),
  };

  const trackActivity = (): void => {
    if (currentOpen && !previousOpen.current) {
      // just open
      trackSidebarActivity('open');
    } else if (currentOpen && submitState === 'submitting') {
      // just hit submit (regardless of result)
      trackSidebarActivity('submit');
    } else if (
      previousOpen.current &&
      !currentOpen &&
      submitState === 'editing'
    ) {
      // just close it without submitting
      trackSidebarActivity('closeWithoutSubmit');
    }
  };

  // Track previous open state and reset when transitioning from closed to open
  useEffect(() => {
    if (
      currentOpen &&
      !previousOpen.current &&
      (submitState === 'submittedOk' || submitState === 'submittedError')
    ) {
      setSubmitState('editing');
    }
    trackActivity();
    previousOpen.current = currentOpen;
  }, [currentOpen, submitState]);

  const handleDismiss = (): void => {
    // Clear auto-close timeout if user manually closes
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
    // Just close the sidebar, don't reset state here to avoid flash
    onClose();
  };

  const handleSubmit = async (values: FormValues): Promise<void> => {
    const url = `/ProductConfig/send-request`;

    const selectedOffers = values.offers
      .filter((offer) => offer.selected)
      .reduce(
        (acc, offer) => {
          acc[offer.title] = offer.details;
          return acc;
        },
        {} as Record<string, string>
      );

    setSubmitState('submitting');

    try {
      await post(url, { request: selectedOffers });
      setSubmitState('submittedOk');
      formik.resetForm();
    } catch (err) {
      Snackbar.error('Something went wrong');
      setSubmitState('submittedError');
    }
  };

  const formik = useFormik<FormValues>({
    initialValues,
    onSubmit: handleSubmit,
    validate: (values: FormValues): FormErrors => {
      const errors: FormErrors = {
        offers: {},
      };

      // Validate each offer in the array
      values.offers.forEach((offer, index) => {
        if (!offer.selected) {
          return;
        }

        if (!offer.details?.trim()) {
          errors.offers[index] = {
            details: offer.requiredErrorMessage,
          };
        } else if (offer.details?.length > 500) {
          errors.offers[index] = {
            details:
              'This field is required to be less than or equal to 500 characters',
          };
        }
      });

      const hasSelected =
        values.offers.filter((offer) => offer.selected).length > 0;
      const hasErrors = Object.keys(errors.offers).length > 0;
      const shouldEnableButton = hasSelected && !hasErrors;
      setButtonOn(shouldEnableButton);

      return errors;
    },
  });

  // Auto-close when in submitted state for 5 seconds
  useEffect(() => {
    if (submitState === 'submittedOk') {
      autoCloseTimeoutRef.current = setTimeout(() => {
        handleDismiss();
      }, 5000);
    }

    // Cleanup
    return (): void => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }
    };
  }, [submitState]);

  const closeIcon = (): JSX.Element => (
    <Icons.Close
      width="24"
      height="24"
      fill={theme.colors.neutral[600]}
      onClick={(): void => {
        handleDismiss();
        formik.resetForm();
      }}
      css={styles.closeIcon}
    />
  );

  return (
    <MuiDrawer
      open={currentOpen}
      onClose={handleDismiss}
      anchor="right"
      css={styles.sidebar(isMobile)}
    >
      {submitState === 'submittedOk' ? (
        <>
          <div css={styles.closeIconContainer}>{closeIcon()}</div>
          <div css={styles.submittedContent}>
            <img src={illustrations.HiFive} css={styles.hifive} />
            <div className="heading1">{SubmittedTitle}</div>
            <div className="body2">{SubmittedDetails}</div>
          </div>
        </>
      ) : (
        <>
          <div css={styles.headerContainer}>
            <div className="heading1">{Title}</div>
            {closeIcon()}
          </div>
          <div className="body2">{Explanation}</div>
          <div>
            {CustomiseOffersOptions.map((option) => (
              <CustomiseOffersCheckbox
                key={option.id}
                formik={formik}
                disabled={submitState === 'submitting'}
                {...option}
              />
            ))}
          </div>
          <div id="buttonControl" css={styles.buttonControl(isMobile)}>
            {isMobile ? (
              <Buttons.Basic
                onClick={onClose}
                css={styles.backButton}
                color="primary"
              >
                <div className="body1">Back</div>
              </Buttons.Basic>
            ) : (
              <></>
            )}
            <Buttons.Primary
              disabled={!buttonOn}
              css={styles.submitButton}
              loading={submitState === 'submitting'}
              onClick={(): Promise<void> => handleSubmit(formik.values)}
            >
              <div className="body1">Submit request</div>
            </Buttons.Primary>
          </div>
        </>
      )}
    </MuiDrawer>
  );
};

export default CustomiseOffersSidebar;
